/**
 * Choosing which printing a card is.
 *
 * Two ways in, in the order a person has them:
 *
 *  1. **What the cascade already offered** - the candidates from a review, each
 *     with the reasons the routes gave it, nearest first.
 *  2. **What is printed on the card** - a set code and a collector number, which
 *     is the one field a human reads without fail and the key the catalog is
 *     indexed by. This is also the whole flow when no capture could be read at
 *     all, which is why it is not hidden behind a "no candidates" branch.
 *
 * Both end in the same place: a printing id the user has agreed to, or typed in,
 * which is all a scan body needs.
 */

import { useState } from 'react';
import type { RecognitionCandidate } from '@unsorted/shared';
import { ApiError, type Api } from '../api/http.js';
import type { PrintingRecord } from '../api/types.js';
import { Banner } from './Banner.js';
import { printingLabel, toChosenPrinting, type ChosenPrinting } from '../scan/queue.js';

export interface PrintingPickerProps {
  api: Api;
  candidates?: RecognitionCandidate[];
  onChoose: (printing: ChosenPrinting) => void;
  /** The prompt on the set/number form, e.g. "The card says". */
  prompt?: string;
}

export function PrintingPicker({ api, candidates = [], onChoose, prompt = 'The card says' }: PrintingPickerProps) {
  const [setCode, setSetCode] = useState('');
  const [number, setNumber] = useState('');
  const [matches, setMatches] = useState<PrintingRecord[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lookup = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setMatches(null);
    try {
      setMatches(await api.lookupPrintings({ setCode, number }));
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'the catalog could not be searched');
    } finally {
      setBusy(false);
    }
  };

  const choose = (printing: PrintingRecord): void =>
    onChoose(
      toChosenPrinting({
        printingId: printing.printingId,
        name: printing.name,
        setCode: printing.setCode,
        collectorNumber: printing.collectorNumber,
      }),
    );

  return (
    <div className="picker">
      {candidates.length > 0 && (
        <>
          <p className="hint">The picture matched these, and cannot say which is yours:</p>
          <ul className="candidates">
            {candidates.map((candidate) => (
              <li key={candidate.printingId}>
                <button type="button" onClick={() => onChoose(toChosenPrinting(candidate))}>
                  {printingLabel({
                    name: candidate.name ?? `Printing ${candidate.printingId}`,
                    setCode: candidate.setCode ?? '',
                    collectorNumber: candidate.collectorNumber ?? '',
                  })}
                  <span className="hint tnum"> score {candidate.score.toFixed(2)}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <form className="row" onSubmit={(event) => void lookup(event)}>
        <label className="grow">
          {prompt} (set code)
          <input
            aria-label={`${prompt} set code`}
            name="setCode"
            autoComplete="off"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            inputMode="text"
            placeholder="M11…"
            value={setCode}
            onChange={(event) => setSetCode(event.target.value)}
            maxLength={16}
            required
          />
        </label>
        <label>
          Number
          <input
            aria-label={`${prompt} collector number`}
            name="collectorNumber"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            inputMode="text"
            placeholder="7…"
            value={number}
            onChange={(event) => setNumber(event.target.value)}
            maxLength={20}
            required
          />
        </label>
        <button type="submit" className="primary" disabled={busy}>
          {busy ? 'Looking…' : 'Find'}
        </button>
      </form>

      {error && <Banner kind="error">{error}</Banner>}
      {matches !== null && matches.length === 0 && (
        <p className="hint" role="status" aria-live="polite">
          The catalog holds no printing of {setCode.toUpperCase()} {number}. Check the number, or the language: only
          printings the catalog carries can be filed.
        </p>
      )}
      {matches !== null && matches.length > 0 && (
        <ul className="candidates">
          {matches.map((printing) => (
            <li key={printing.printingId}>
              <button type="button" onClick={() => choose(printing)}>
                {printingLabel(printing)}
                {printing.lang === 'en' ? null : <span className="hint"> {printing.lang}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
